import { useRef, useState } from "react";
import { toast } from "sonner";
import type { PurchaseShipment } from "@/api/purchase";
import { useKorrigiereShipmentStatus, useSetPurchaseShipmentStatus } from "@/api/purchase";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { shipmentStatusLabel } from "@/lib/purchase-format";
import { fehlerText } from "@/lib/fehlerText";
import { KORREKTUR_GRUND_MIN, korrekturZiele, prozessKnoepfe } from "./bestand-buchen";

/**
 * Plan 2026-10-19 (d): Container-Status nur ueber Prozessknoepfe (kein Dropdown). Zustellung/WE laufen
 * ueber die WE-Bruecke, „eingelagert“ nur ueber „Bestand buchen“. Rueckwaerts vor dem WE geht nur
 * ueber „Status korrigieren…“ mit Begruendung (Preisrecht).
 */
export function ContainerStatusAktionen({
  shipment, darfKorrigieren, gesperrt,
}: { shipment: PurchaseShipment; darfKorrigieren: boolean; gesperrt: boolean }) {
  const setStatus = useSetPurchaseShipmentStatus();
  const korrigiere = useKorrigiereShipmentStatus();
  const laeuft = useRef(false);
  const [korrekturOffen, setKorrekturOffen] = useState(false);
  const knoepfe = prozessKnoepfe(shipment);
  const ziele = korrekturZiele(shipment);

  const wechsel = (ziel: PurchaseShipment["status"]) => {
    if (laeuft.current) return;
    laeuft.current = true;
    setStatus.mutate({ id: shipment.id, status: ziel }, {
      onSuccess: () => toast.success(`Container-Status: ${shipmentStatusLabel(ziel)}`),
      onError: (e) => toast.error(fehlerText(e, "Container-Status konnte nicht geändert werden")),
      onSettled: () => { laeuft.current = false; },
    });
  };

  const fortsetzenOhneZiel = shipment.status === "geparkt" && knoepfe.length === 0 && shipment.warehouse_receipt_id === null;

  return (
    <div role="group" aria-label={`Containerstatus Container ${shipment.id}`} className="flex flex-wrap items-center gap-2 text-xs text-ink-2">
      <span>Containerstatus</span>
      {knoepfe.map((k) => (
        <Button
          key={k.ziel}
          type="button"
          size="sm"
          variant="outline"
          disabled={gesperrt || setStatus.isPending}
          title={k.label === "Fortsetzen" ? `Zurück zu „${shipmentStatusLabel(k.ziel)}“` : undefined}
          onClick={() => wechsel(k.ziel)}
        >
          {k.label}
        </Button>
      ))}
      {fortsetzenOhneZiel ? (
        <span>Status vor dem Parken unbekannt — über „Status korrigieren…“ setzen.</span>
      ) : null}
      {darfKorrigieren && ziele.length > 0 ? (
        <Button type="button" size="sm" variant="ghost" disabled={gesperrt} onClick={() => setKorrekturOffen(true)}>
          Status korrigieren…
        </Button>
      ) : null}
      {korrekturOffen ? (
        <KorrekturDialog
          shipment={shipment}
          ziele={ziele}
          busy={korrigiere.isPending}
          onClose={() => setKorrekturOffen(false)}
          onSpeichern={(status, grund, setFehler) => korrigiere.mutate({ id: shipment.id, status, grund }, {
            onSuccess: () => { toast.success(`Status korrigiert: ${shipmentStatusLabel(status)}`); setKorrekturOffen(false); },
            onError: (e) => setFehler(fehlerText(e, "Status konnte nicht korrigiert werden")),
          })}
        />
      ) : null}
    </div>
  );
}

function KorrekturDialog({
  shipment, ziele, busy, onClose, onSpeichern,
}: {
  shipment: PurchaseShipment;
  ziele: PurchaseShipment["status"][];
  busy: boolean;
  onClose: () => void;
  onSpeichern: (status: PurchaseShipment["status"], grund: string, setFehler: (t: string) => void) => void;
}) {
  const [ziel, setZiel] = useState<PurchaseShipment["status"]>(ziele[ziele.length - 1]!);
  const [grund, setGrund] = useState("");
  const [fehler, setFehler] = useState<string | null>(null);
  const grundOk = grund.trim().length >= KORREKTUR_GRUND_MIN;
  return (
    <Dialog open onOpenChange={(o) => { if (!o && !busy) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Status korrigieren</DialogTitle>
          <DialogDescription>
            Nur rückwärts und nur vor dem Wareneingang. Aktuell: „{shipmentStatusLabel(shipment.status)}“. Die Begründung steht im Verlauf.
          </DialogDescription>
        </DialogHeader>
        <label className="grid gap-1 text-sm text-ink">
          Neuer Status
          <select
            value={ziel}
            onChange={(e) => setZiel(e.target.value as PurchaseShipment["status"])}
            className="rounded border border-line bg-background px-2 py-1 text-sm text-ink"
          >
            {ziele.map((z) => <option key={z} value={z}>{shipmentStatusLabel(z)}</option>)}
          </select>
        </label>
        <label className="grid gap-1 text-sm text-ink">
          Begründung (mindestens {KORREKTUR_GRUND_MIN} Zeichen)
          <Textarea value={grund} onChange={(e) => setGrund(e.target.value)} rows={3} maxLength={500} />
        </label>
        {fehler ? <p role="alert" className="text-sm text-coral">{fehler}</p> : null}
        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" size="sm" disabled={busy} onClick={onClose}>Abbrechen</Button>
          <Button
            type="button"
            size="sm"
            disabled={busy || !grundOk}
            onClick={() => { setFehler(null); onSpeichern(ziel, grund, setFehler); }}
          >
            Korrigieren
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
